// src/pages/AdminLeads.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { 
  FaPlus, FaSearch, FaEnvelope, FaExternalLinkAlt, 
  FaEdit, FaTrash, FaArchive, FaPaperPlane, FaCheck, FaSpinner 
} from 'react-icons/fa';

interface Lead {
  id: string;
  company_name: string;
  email: string;
  demo_slug: string;
  status: 'new' | 'sent' | 'viewed' | 'archived';
  created_at: string;
}

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSending, setIsSending] = useState<string | null>(null); // Pour l'animation d'envoi

  // États pour le nouveau lead
  const [newCompany, setNewCompany] = useState("");
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('sdr_leads')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (data) setLeads(data);
    setLoading(false);
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    const slug = generateSlug(newCompany);
    const { error } = await supabase.from('sdr_leads').insert([
      { company_name: newCompany, email: newEmail, demo_slug: slug, status: 'new' }
    ]);

    if (!error) {
      setNewCompany("");
      setNewEmail("");
      fetchLeads();
    }
  };

  const handleSendEmail = async (lead: Lead) => {
    setIsSending(lead.id);
    try {
      // Appel à ta Edge Function Supabase
      const { data, error } = await supabase.functions.invoke('send-sdr-email', {
        body: { 
          leadId: lead.id, 
          email: lead.email, 
          companyName: lead.company_name,
          demoUrl: `https://forfeoexplore.com/demo/${lead.demo_slug}` 
        }
      });

      if (!error) {
        await supabase.from('sdr_leads').update({ status: 'sent' }).eq('id', lead.id);
        fetchLeads();
      } else {
        alert("Erreur lors de l'envoi : " + error.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSending(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer ce lead ?")) {
      await supabase.from('sdr_leads').delete().eq('id', id);
      fetchLeads();
    }
  };

  const filteredLeads = leads.filter(l => 
    l.company_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    l.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-10 font-sans">
      <div className="max-w-6xl mx-auto">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">SDR Command Center</h1>
            <p className="text-slate-500 font-medium">Gérez vos prospects et envoyez vos démos Forfeo.</p>
          </div>
          <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-slate-200 w-full md:w-auto">
            <div className="px-4 flex items-center text-slate-400"><FaSearch/></div>
            <input 
              type="text" 
              placeholder="Rechercher un établissement..." 
              className="border-none focus:ring-0 py-2 bg-transparent font-bold text-sm w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* AJOUT RAPIDE */}
        <form onSubmit={handleAddLead} className="bg-slate-900 rounded-[2rem] p-6 mb-10 shadow-xl flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-2 mb-2 block">Nom de l'entreprise</label>
            <input 
              required
              type="text" 
              placeholder="Ex: Hotels Jaro" 
              className="w-full bg-slate-800 border-none rounded-xl text-white font-bold p-3 focus:ring-2 focus:ring-blue-500"
              value={newCompany}
              onChange={(e) => setNewCompany(e.target.value)}
            />
          </div>
          <div className="flex-1 w-full">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-2 mb-2 block">Email Contact</label>
            <input 
              required
              type="email" 
              placeholder="directeur@hotel.com" 
              className="w-full bg-slate-800 border-none rounded-xl text-white font-bold p-3 focus:ring-2 focus:ring-blue-500"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>
          <button type="submit" className="bg-blue-500 hover:bg-blue-600 text-white h-[48px] px-8 rounded-xl font-black uppercase tracking-widest text-xs transition-all flex items-center gap-2">
            <FaPlus/> Créer la démo
          </button>
        </form>

        {/* LISTE DES LEADS */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-5 text-[10px] font-black uppercase text-slate-400">Établissement</th>
                <th className="p-5 text-[10px] font-black uppercase text-slate-400">Status</th>
                <th className="p-5 text-[10px] font-black uppercase text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={3} className="p-10 text-center text-slate-400 font-bold">Chargement des données...</td></tr>
              ) : filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50/50 transition">
                  <td className="p-5">
                    <div className="font-black text-slate-800">{lead.company_name}</div>
                    <div className="text-xs text-slate-400 font-medium">{lead.email}</div>
                  </td>
                  <td className="p-5">
                    <span className={`
                      text-[9px] font-black uppercase px-3 py-1 rounded-full border
                      ${lead.status === 'new' ? 'bg-blue-50 text-blue-600 border-blue-100' : ''}
                      ${lead.status === 'sent' ? 'bg-amber-50 text-amber-600 border-amber-100' : ''}
                      ${lead.status === 'viewed' ? 'bg-green-50 text-green-600 border-green-100' : ''}
                      ${lead.status === 'archived' ? 'bg-slate-50 text-slate-400 border-slate-100' : ''}
                    `}>
                      {lead.status === 'new' ? 'Nouveau' : lead.status === 'sent' ? 'Email Envoyé' : lead.status === 'viewed' ? 'Démo Ouverte' : 'Archivé'}
                    </span>
                  </td>
                  <td className="p-5">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => window.open(`/demo/${lead.demo_slug}`, '_blank')}
                        className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition"
                        title="Voir la démo"
                      >
                        <FaExternalLinkAlt size={14}/>
                      </button>
                      <button 
                        onClick={() => handleSendEmail(lead)}
                        disabled={isSending === lead.id}
                        className={`px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition
                          ${lead.status === 'sent' || lead.status === 'viewed' 
                            ? 'bg-slate-100 text-slate-400' 
                            : 'bg-slate-900 text-white hover:bg-blue-600 shadow-lg'}
                        `}
                      >
                        {isSending === lead.id ? <FaSpinner className="animate-spin"/> : <FaPaperPlane/>}
                        {lead.status === 'sent' || lead.status === 'viewed' ? 'Renvoyer' : 'Envoyer'}
                      </button>
                      <button 
                        onClick={() => handleDelete(lead.id)}
                        className="p-2 text-slate-300 hover:text-red-500 transition"
                      >
                        <FaTrash size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
